import { useEffect, useMemo, useState } from "react";

import { Button, CloseSVG, Icon, Modal, useModal } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useRouter } from "next/router";

import { ContentLevel } from "@/types/models/ContentLevel.interface";

import {
  CitationFormInputKey,
  ICitationFormInput,
  IContentData,
} from "../CitationAction.interface";
import CitationEditForm from "../CitationEditForm/CitationEditForm";
import CitationGenerator from "../CitationGenerator/CitationGenerator";
import { setUpCitationState } from "../utils";
import {
  wrapperCss as citationModalWrapperCss,
  closeButtonCss,
  headerTitleCss,
  modalCss,
  modalHeaderCss,
} from "./CitationModal.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface CitationModalProps {
  active: boolean;
  setActive: (active: boolean) => void;
  currentLevel: ContentLevel | undefined;
  contentData: IContentData;
}

const CitationModal = ({
  active,
  setActive,
  contentData,
  currentLevel,
}: CitationModalProps) => {
  const router = useRouter();
  const [modalProps] = useModal(true);
  const [showEditForm, setShowEditForm] = useState(false);

  const lastPublication = useMemo(
    () => contentData?.article_publications?.slice(-1)[0]?.visible_after,
    [contentData?.article_publications],
  );

  const [citationData, setCitationData] = useState(
    setUpCitationState(contentData, currentLevel, router, lastPublication),
  );

  useEffect(() => {
    setCitationData(
      setUpCitationState(contentData, currentLevel, router, lastPublication),
    );
  }, [contentData, currentLevel]);

  const updateCitationData = (key: CitationFormInputKey, value: string) => {
    setCitationData((prev) => ({ ...prev, [key]: value }));
  };

  const handleDismiss = () => {
    setShowEditForm(false);
    setActive(false);
  };

  const handleEditChanges = (data: ICitationFormInput) => {
    Object.entries(data).map(([key, value]) =>
      updateCitationData(key as CitationFormInputKey, value),
    );
  };

  const renderHeader = () => (
    <div css={modalHeaderCss}>
      <h3 css={headerTitleCss}>Citation generator</h3>
      <ButtonWithTrackEvent
        flavor={Button.flavors.incognito}
        actionName={actionNames.cancel}
        onClick={handleDismiss}
        objectType={objectTypes.generatedTextAsset}
        dataProps={{
          "data-qa-selector": "citation-close-button",
        }}
        __cssFor={{ root: closeButtonCss }}
      >
        <Icon SvgComponent={CloseSVG} />
      </ButtonWithTrackEvent>
    </div>
  );

  const renderBody = () => {
    return showEditForm ? (
      <CitationEditForm
        setShowEditForm={setShowEditForm}
        handleEditChanges={handleEditChanges}
        citationData={citationData}
      />
    ) : (
      <CitationGenerator
        setShowEditForm={setShowEditForm}
        citationData={citationData}
        handleDismiss={handleDismiss}
      />
    );
  };

  return (
    <Modal
      {...modalProps}
      onBackgroundClick={handleDismiss}
      isActive={active}
      ariaProps={{
        "aria-labelledby": "modal_label",
      }}
      __cssFor={{ root: modalCss }}
    >
      <div css={citationModalWrapperCss}>
        {renderHeader()}
        {renderBody()}
      </div>
    </Modal>
  );
};

export default CitationModal;
